import { useState } from "react";
import PrimaryButton from "../../../components/buttons/PrimaryButton";

interface Expert {
  data: {
    name: string;
    family_name: string;
  };
  email: string;
}

interface ExpertFilterDropdownProps {
  experts: Expert[];
  selectedExpert: string | null;
  placeholder: string;
  onExpertSelect: (name: string, email: string) => void;
  onReset: () => void;
  resetText: string;
  compact?: boolean;
}

export const ExpertFilterDropdown: React.FC<ExpertFilterDropdownProps> = ({
  experts,
  selectedExpert,
  placeholder,
  onExpertSelect,
  onReset,
  resetText,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleExpertClick = (name: string, email: string) => {
    onExpertSelect(name, email);
    setIsOpen(false);
  };

  const handleReset = () => {
    onReset();
    setIsOpen(false);
  };

  const getSelectedExpertName = () => {
    if (!selectedExpert) return placeholder;
    const expert = experts.find(e => e.email === selectedExpert);
    return expert ? `${expert.data.name} ${expert.data.family_name}` : placeholder;
  };

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={handleToggle}
          className="w-full sm:w-[180px] md:w-[200px] lg:w-[220px] h-[40px] px-3 py-2 text-sm bg-holifya-blue text-white rounded-[5px] hover:bg-blue-700 transition-colors duration-200 flex items-center justify-between"
        >
          <span className="truncate text-left">
            {getSelectedExpertName()}
          </span>
          <svg 
            className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute top-[45px] right-0 bg-white border border-solid border-gray-300 rounded shadow-lg z-10 min-w-[200px] max-h-[300px] overflow-y-auto">
            {experts.map((expert, index) => (
              <div
                key={index}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                onClick={() => handleExpertClick(`${expert.data.name} ${expert.data.family_name}`, expert.email)}
              >
                {expert.data.name} {expert.data.family_name}
              </div>
            ))}
            <div
              className="bg-gray-100 px-3 py-2 cursor-pointer hover:bg-gray-200 text-sm border-t border-gray-200"
              onClick={handleReset}
            >
              {resetText}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-[250px] h-[50px] relative">
      <PrimaryButton
        text={getSelectedExpertName()}
        pointer={true}
        disabled={false}
        onClick={handleToggle}
      />
      {isOpen && (
        <div className="absolute top-[60px] right-0 bg-white border border-solid border-gray-300 rounded shadow-lg z-10 min-w-[250px]">
          {experts.map((expert, index) => (
            <div
              key={index}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
              onClick={() => handleExpertClick(`${expert.data.name} ${expert.data.family_name}`, expert.email)}
            >
              {expert.data.name} {expert.data.family_name}
            </div>
          ))}
          <div
            className="bg-gray-100 px-4 py-2 cursor-pointer hover:bg-gray-200"
            onClick={handleReset}
          >
            {resetText}
          </div>
        </div>
      )}
    </div>
  );
}; 